import { useEffect, useRef, useCallback, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { clientDetailStore } from "@/stores/ClientDetailStore";
import { ROUTES } from "@/constants/routes";
import ClientHeader from "./components/ClientHeader";
import ClientMetrics from "./components/ClientMetrics";
import TransactionsTab from "./components/TransactionsTab";
import BookingsTab from "./components/BookingsTab";

import "./ClientDetailPage.css";
import TransactionDetailModal from "@/shared/ui/DetailModal/TransactionDetailModal";

const TABS = [
  { id: "transactions", label: "История операций" },
  { id: "bookings", label: "Бронирования" },
] as const;

type TabId = (typeof TABS)[number]["id"];

const ClientDetailPage: React.FC = observer(() => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const store = clientDetailStore;
  const [activeTab, setActiveTab] = useState<TabId>("transactions");
  const [visitedTabs, setVisitedTabs] = useState<Set<TabId>>(
    new Set<TabId>(["transactions"]),
  );
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    scrollContainerRef.current = document.querySelector(
      ".app-layout__content",
    ) as HTMLElement | null;
  }, []);

  const handleTabChange = useCallback((tabId: TabId) => {
    const el = scrollContainerRef.current;
    const saved = el?.scrollTop ?? 0;
    setVisitedTabs((prev) => new Set([...prev, tabId]));
    setActiveTab(tabId);
    if (el)
      requestAnimationFrame(() => {
        el.scrollTop = saved;
      });
  }, []);

  useEffect(() => {
    if (uuid) {
      store.loadClient(uuid);
      store.loadStats(uuid);
    }
    return () => {
      store.reset();
    };
  }, [uuid]);

  if (store.isLoading) {
    return (
      <div className="cdp-page">
        <div className="cdp-page__loading">Загрузка...</div>
      </div>
    );
  }

  if (!store.client) {
    return (
      <div className="cdp-page">
        <div className="cdp-page__not-found">
          <p>Клиент не найден</p>
          <button
            className="cdp-back"
            onClick={() => navigate(ROUTES.CLIENTS)}
          >
            Вернуться к списку
          </button>
        </div>
      </div>
    );
  }

  const activeTabIndex = TABS.findIndex((t) => t.id === activeTab);

  return (
    <div
      className="cdp-page cdp-page--client"
      data-testid="cdp-detail-page"
    >
      <ClientHeader />
      <ClientMetrics />

      <div className="cdp-tabs-wrap">
        <div className="cdp-tabs-top-bar">
          <div
            className="cdp-tabs"
            role="tablist"
            aria-label="Разделы клиента"
          >
            <div className="cdp-tabs__list-outer">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  id={`tab-${tab.id}`}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`cdp-tabs__tab${activeTab === tab.id ? " cdp-tabs__tab--active" : ""}`}
                  onClick={() => handleTabChange(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
              <div
                className="cdp-tabs__indicator"
                aria-hidden="true"
                style={{ transform: `translateX(${activeTabIndex * 100}%)` }}
              />
            </div>
          </div>
        </div>

        <div className="cdp-tabs__panels">
          {visitedTabs.has("transactions") && (
            <div
              id="tabpanel-transactions"
              role="tabpanel"
              className="cdp-tabpanel-flex"
              style={{
                display: activeTab === "transactions" ? "flex" : "none",
              }}
            >
              <TransactionsTab />
            </div>
          )}
          {visitedTabs.has("bookings") && (
            <div
              id="tabpanel-bookings"
              role="tabpanel"
              className="cdp-tabpanel-flex"
              style={{ display: activeTab === "bookings" ? "flex" : "none" }}
            >
              <BookingsTab />
            </div>
          )}
        </div>
      </div>

      <TransactionDetailModal pd={store.pd} />
    </div>
  );
});

ClientDetailPage.displayName = "ClientDetailPage";
export default ClientDetailPage;
